import React from "react";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import Dialog from "@mui/material/Dialog";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";

import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";

import { useFormik } from "formik";
import * as yup from "yup";

import { EntitlementDuration, UserResource } from "../../api/users";
import { useSnackbar } from "../../hooks/useSnackbar";

interface AddUserFields {
  email: string;
  nickname: string;
  entitlementDuration: EntitlementDuration;
}

// Paid-access options. "None" creates the account without any entitlement.
const DURATION_OPTIONS: { value: EntitlementDuration; label: string }[] = [
  { value: "daily", label: "A day" },
  { value: "three_day", label: "Three days" },
  { value: "weekly", label: "A week" },
  { value: "monthly", label: "A month" },
  { value: "three_month", label: "Three months" },
  { value: "six_month", label: "Six months" },
  { value: "yearly", label: "A year" },
  { value: "lifetime", label: "Lifetime" },
  { value: "none", label: "None" },
];

interface AddUserFormProperties {
  onClose: () => void;
  onSuccess: (message: string) => void;
}

export const AddUserForm: React.FC<AddUserFormProperties> = ({
  onClose,
  onSuccess,
}) => {
  const [errorMessage, setErrorMessage] = React.useState<string>();

  const initialValues: AddUserFields = {
    email: "",
    nickname: "",
    entitlementDuration: "none",
  };

  const validationSchema = yup.object({
    email: yup.string().email().required("A valid email is required."),
    nickname: yup.string().required(),
    entitlementDuration: yup.string().required(),
  });

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: async (values) => {
      setErrorMessage(undefined);
      try {
        const result = await UserResource.createUser({
          email: values.email.trim(),
          displayName: values.nickname.trim(),
          entitlementDuration: values.entitlementDuration,
        });
        formik.resetForm();
        onSuccess(
          result.entitlementGranted
            ? "User created with paid access."
            : "User created."
        );
        onClose();
      } catch (error) {
        setErrorMessage(
          `Could not create user! Error: ${error instanceof Error ? error.message : String(error)}`
        );
      }
    },
  });

  return (
    <Card
      style={{
        padding: "1rem",
      }}
    >
      <Box
        style={{
          display: "flex",
          flexDirection: "row",
        }}
      >
        <Typography variant="h4" style={{ flex: 1, paddingBottom: "1rem" }}>
          Create a New Focused User
        </Typography>
        <IconButton onClick={onClose}>
          <CloseOutlinedIcon />
        </IconButton>
      </Box>
      <Box>
        {errorMessage && <Typography variant="h6">{errorMessage}</Typography>}
      </Box>
      <Box>
        <form onSubmit={formik.handleSubmit}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                id="nickname"
                name="nickname"
                label="Name"
                value={formik.values.nickname}
                onChange={formik.handleChange}
                error={
                  formik.touched.nickname && Boolean(formik.errors.nickname)
                }
                helperText={formik.touched.nickname && formik.errors.nickname}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                id="email"
                name="email"
                label="Email"
                value={formik.values.email}
                onChange={formik.handleChange}
                error={formik.touched.email && Boolean(formik.errors.email)}
                helperText={formik.touched.email && formik.errors.email}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                select
                fullWidth
                id="entitlementDuration"
                name="entitlementDuration"
                label="Access duration"
                value={formik.values.entitlementDuration}
                onChange={formik.handleChange}
                helperText="Grants paid access for this duration. None creates the user with no paid access."
              >
                {DURATION_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Button
                color="primary"
                variant="contained"
                fullWidth
                type="submit"
                disabled={formik.isSubmitting}
              >
                Create user
              </Button>
            </Grid>
          </Grid>
        </form>
      </Box>
    </Card>
  );
};

export const AddUser = () => {
  const [formVisible, setFormVisible] = React.useState(false);
  const { showSnackbar, SnackbarNode } = useSnackbar();

  return (
    <Box>
      <IconButton
        onClick={(e) => {
          e.preventDefault();
          setFormVisible(true);
        }}
      >
        <AddCircleOutlineIcon />
      </IconButton>
      <Dialog open={formVisible}>
        <AddUserForm
          onClose={() => setFormVisible(false)}
          onSuccess={(message) => showSnackbar(message, "success")}
        />
      </Dialog>
      {SnackbarNode}
    </Box>
  );
};
